import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { ArrowLeft, Sparkles, Gift, Users, Calendar, Heart, CheckCircle2, ArrowRight } from 'lucide-react';

const REWARD_PREVIEW = [
  { code: 'FREE_HELIUM',   name: 'Free Helium Balloons (6)', cost: 200, kind: 'Freebie',  emoji: '🎈' },
  { code: 'EXTRA_CRAFT',   name: 'Free Extra Craft Station', cost: 350, kind: 'Freebie',  emoji: '🎨' },
  { code: 'FREE_CUPCAKES', name: 'Free Cupcakes (6)',         cost: 400, kind: 'Freebie',  emoji: '🧁' },
  { code: 'DISCOUNT_25',   name: '$25 off your next party',   cost: 500, kind: 'Discount', emoji: '💸' },
  { code: 'FREE_TSHIRT',   name: 'Free T-Shirt Print',        cost: 800, kind: 'Freebie',  emoji: '👕' },
];

const Step = ({ n, icon: Icon, title, body, accent }) => (
  <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 hover:shadow-md transition-shadow">
    <div className="flex items-start gap-3">
      <div
        className="flex-none w-11 h-11 rounded-2xl flex items-center justify-center text-white font-bold"
        style={{ background: accent }}
        aria-hidden
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B6B7A]">Step {n}</p>
        <h3 className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{title}</h3>
        <p className="text-sm text-[#6B6B7A] mt-1">{body}</p>
      </div>
    </div>
  </div>
);

const LoyaltyInfo = () => {
  return (
    <div className="min-h-screen bg-[#FFFDF8]" data-testid="loyalty-info-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFE9EE 0%, #FFF4D6 50%, #FFE9EE 100%)' }}>
        <div className="container mx-auto px-4 py-12 sm:py-16">
          <Link to="/" className="inline-flex items-center text-sm text-[#6B6B7A] hover:text-[#1E1E24] mb-5" data-testid="loyalty-back-home">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Home
          </Link>
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#FF3B6B]"
            >
              <Sparkles className="w-3 h-3" /> Loyalty Program
            </motion.div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }} data-testid="loyalty-headline">
              Earn points just by <span className="text-[#FF3B6B]">celebrating.</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-[#1E1E24] max-w-xl">
              Every Creative Birthdays customer earns reward points automatically. No sign-up fee, no card to carry, no expiry games.
              Use them whenever you're ready for the next party.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/book">
                <Button className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white px-6 h-12" data-testid="loyalty-cta-book">
                  Start earning <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/my-loyalty">
                <Button variant="outline" className="rounded-full h-12 px-6" data-testid="loyalty-cta-account">
                  See my balance
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="container mx-auto px-4 py-14">
        <h2 className="text-3xl font-bold text-[#1E1E24] text-center" style={{ fontFamily: 'Fredoka' }}>How it works</h2>
        <p className="text-sm text-[#6B6B7A] text-center mt-1">Three simple steps. Same flow for every customer.</p>
        <div className="grid sm:grid-cols-3 gap-4 mt-7 max-w-4xl mx-auto">
          <Step n={1} icon={Calendar} accent="linear-gradient(135deg, #FF3B6B 0%, #FB923C 100%)" title="Book any party"
                body="The moment your party balance is fully paid, you earn 10 points for every $1 of subtotal." />
          <Step n={2} icon={Gift}     accent="linear-gradient(135deg, #FB923C 0%, #FFD166 100%)" title="Watch points stack"
                body="Your balance is always visible in your account at /my-loyalty. Points never expire — celebrate whenever you're ready." />
          <Step n={3} icon={Sparkles} accent="linear-gradient(135deg, #06D6A0 0%, #6366F1 100%)" title="Redeem at checkout"
                body="Pick a freebie or discount voucher and apply it on your next booking. The savings come straight off your subtotal." />
        </div>
      </section>

      {/* Reward menu */}
      <section className="container mx-auto px-4 pb-14">
        <h2 className="text-3xl font-bold text-[#1E1E24] text-center" style={{ fontFamily: 'Fredoka' }}>Reward menu</h2>
        <p className="text-sm text-[#6B6B7A] text-center mt-1">A taste of what your points unlock.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-7 max-w-4xl mx-auto" data-testid="loyalty-rewards-preview">
          {REWARD_PREVIEW.map((r) => (
            <div key={r.code} className="bg-white rounded-2xl border border-[#E8E5DF] p-4 flex items-center gap-3">
              <div className="text-3xl flex-none" aria-hidden>{r.emoji}</div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">{r.kind}</p>
                <p className="font-semibold text-[#1E1E24] truncate" style={{ fontFamily: 'Fredoka' }}>{r.name}</p>
              </div>
              <p className="font-bold text-[#FF3B6B] text-sm whitespace-nowrap">{r.cost} pts</p>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-[#6B6B7A] mt-3">Your full live menu is at <Link to="/my-loyalty" className="underline">/my-loyalty</Link>.</p>
      </section>

      {/* Bonus points */}
      <section className="bg-white border-y border-[#E8E5DF]">
        <div className="container mx-auto px-4 py-14">
          <h2 className="text-3xl font-bold text-[#1E1E24] text-center" style={{ fontFamily: 'Fredoka' }}>Earn bonus points</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-7 max-w-3xl mx-auto">
            <div className="bg-[#FFFDF8] rounded-2xl p-5 border border-[#E8E5DF]">
              <Users className="w-8 h-8 text-[#6366F1]" />
              <h3 className="mt-3 text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>+100 pts per friend referred</h3>
              <p className="text-sm text-[#6B6B7A] mt-1">Every customer has a unique referral code (REF-XXXXXX). The moment a new friend books with your code, 100 points land in your account. No limit.</p>
            </div>
            <div className="bg-[#FFFDF8] rounded-2xl p-5 border border-[#E8E5DF]">
              <Heart className="w-8 h-8 text-[#FF3B6B]" />
              <h3 className="mt-3 text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Surprise drops</h3>
              <p className="text-sm text-[#6B6B7A] mt-1">From time to time we sprinkle bonus points into customer accounts as a thank-you — birthdays, holidays, or simply because. We'll always email you when it happens.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why free */}
      <section className="container mx-auto px-4 py-14">
        <div className="max-w-2xl mx-auto bg-white rounded-3xl border border-[#E8E5DF] p-7">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Wait, is this really free?</h2>
          <p className="text-sm text-[#1E1E24] mt-3">
            Yes. There's no signup fee, no membership tier, no annual cost. Loyalty is built into every Creative Birthdays customer account
            — you're enrolled the moment you book your first party. We make our money on the parties we host; the points are just our way
            of saying <em>"thank you for picking us again"</em>.
          </p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {[
              'No fee to join',
              'Points never expire',
              'Stack with promo codes',
              'Apply at checkout in one tap',
            ].map((t) => (
              <p key={t} className="flex items-center gap-2 text-[#1E1E24]"><CheckCircle2 className="w-4 h-4 text-[#06D6A0] flex-none" />{t}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#1E1E24] text-white">
        <div className="container mx-auto px-4 py-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Fredoka' }}>Ready to start earning?</h2>
          <p className="text-sm text-white/75 mt-2">Book a party today — your first batch of points lands in your account the moment your balance is paid.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/book">
              <Button className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white px-7 h-12">Book a party <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="rounded-full h-12 px-6 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoyaltyInfo;
